import { Timestamp } from 'firebase/firestore';
import { AppError as AppErrorType } from '@/types';
import { auth } from '@/services/firebase';

/**
 * Standard error codes for the application
 */
export enum ErrorCode {
  // Validation Errors
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  INVALID_INPUT = 'INVALID_INPUT',
  MISSING_REQUIRED = 'MISSING_REQUIRED',
  
  // Authentication/Authorization Errors
  UNAUTHENTICATED = 'UNAUTHENTICATED',
  UNAUTHORIZED = 'UNAUTHORIZED',
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  
  // Resource Errors
  NOT_FOUND = 'NOT_FOUND',
  ALREADY_EXISTS = 'ALREADY_EXISTS',
  RESOURCE_EXHAUSTED = 'RESOURCE_EXHAUSTED',
  
  // Operation Errors
  OPERATION_FAILED = 'OPERATION_FAILED',
  CANCELLED = 'CANCELLED',
  TIMEOUT = 'TIMEOUT',
  
  // System Errors
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

/**
 * Custom Application Error class
 */
export class AppError extends Error implements AppErrorType {
  public code: string;
  public details?: { [key: string]: any };
  public timestamp: Timestamp;
  public userId?: string;
  public originalError?: any;

  constructor(
    code: string,
    message: string,
    details?: { [key: string]: any },
    originalError?: any
  ) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.details = details;
    this.timestamp = Timestamp.now();
    this.originalError = originalError;

    // Capture stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }

    // Try to get current user ID
    this.userId = auth.currentUser?.uid;
  }

  public toJSON() {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      details: this.details,
      timestamp: this.timestamp,
      userId: this.userId,
      stack: this.stack
    };
  }
}

/**
 * Helper to convert Firestore/Firebase errors to AppError
 */
export function handleFirebaseError(error: any, context?: string): AppError {
  console.error(`Firebase Error [${context || 'Unknown Context'}]:`, error);

  let code = ErrorCode.UNKNOWN_ERROR;
  let message = error.message || 'An unexpected error occurred';
  const details: any = { originalCode: error.code };

  if (error.code) {
    switch (error.code) {
      // Firestore / General Permission
      case 'permission-denied':
      case 'auth/insufficient-permission':
        code = ErrorCode.PERMISSION_DENIED;
        message = 'You do not have permission to perform this action.';
        break;
      
      case 'unauthenticated':
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        code = ErrorCode.UNAUTHENTICATED;
        message = 'Authentication required or failed.';
        break;

      case 'not-found':
      case 'storage/object-not-found':
        code = ErrorCode.NOT_FOUND;
        message = 'The requested resource was not found.';
        break;

      case 'already-exists':
      case 'auth/email-already-in-use':
        code = ErrorCode.ALREADY_EXISTS;
        message = 'The resource already exists.';
        break;

      case 'resource-exhausted':
      case 'storage/quota-exceeded':
        code = ErrorCode.RESOURCE_EXHAUSTED;
        message = 'Quota exceeded or resource exhausted.';
        break;

      case 'cancelled':
        code = ErrorCode.CANCELLED;
        message = 'The operation was cancelled.';
        break;

      case 'deadline-exceeded':
        code = ErrorCode.TIMEOUT;
        message = 'The operation timed out.';
        break;

      case 'unavailable':
      case 'auth/network-request-failed':
        code = ErrorCode.NETWORK_ERROR;
        message = 'Network connection lost or service unavailable.';
        break;

      case 'failed-precondition':
      case 'out-of-range':
      case 'invalid-argument':
        code = ErrorCode.INVALID_INPUT;
        message = 'Invalid input or precondition failed.';
        break;
        
      case 'unimplemented':
        code = ErrorCode.INTERNAL_ERROR;
        message = 'This feature is not implemented yet.';
        break;

      default:
        // Keep unknown code but try to use the message
        break;
    }
  }

  return new AppError(code, message, details, error);
}

/**
 * Helper to convert Validation (Yup) errors to AppError
 */
export function handleValidationError(error: any): AppError {
  console.warn('Validation Error:', error);

  const details: any = {};
  
  if (error.inner && Array.isArray(error.inner)) {
    error.inner.forEach((err: any) => {
      if (err.path) {
        details[err.path] = err.message;
      }
    });
  }

  return new AppError(
    ErrorCode.VALIDATION_ERROR,
    error.message || 'Validation failed',
    details,
    error
  );
}

/**
 * Generic error handler that determines the error type and returns an AppError
 */
export function handleError(error: any, context?: string): AppError {
  if (error instanceof AppError) {
    return error;
  }

  if (error.name === 'ValidationError') {
    return handleValidationError(error);
  }

  if (error.code && (error.code.startsWith('auth/') || error.code.startsWith('storage/') || error.message?.includes('firestore'))) {
    return handleFirebaseError(error, context);
  }
  
  // Check if it's a Firebase error object (has code property) even if we didn't catch it above
  if (typeof error === 'object' && error !== null && 'code' in error) {
     return handleFirebaseError(error, context);
  }

  console.error(`System Error [${context || 'Unknown Context'}]:`, error);
  return new AppError(
    ErrorCode.UNKNOWN_ERROR,
    error.message || 'An unexpected system error occurred',
    { originalError: error },
    error
  );
}
